import { c as _c } from "react/compiler-runtime";
import { LinkButton, LoadingSpinner, PageShell } from '@onefootprint/ui';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { PasskeyProcessingResult, type PasskeyProcessingResultProps } from '@/idv2/features/identify';
import useD2pStatusPoll, { isTerminalStatus } from '@/idv2/hooks/use-d2p-status-poll';
import { useFootprint } from '@/idv2/hooks/use-footprint';
import { getLogger } from '@/idv2/utils/logger';
const {
  logInfo,
  logWarn
} = getLogger({
  name: 'idv2',
  location: 'passkey-processing'
});
const processingSearchSchema = z.object({
  authToken: z.string(),
  validationToken: z.string(),
  scopedAuthToken: z.string()
});
export const Route = createFileRoute('/identify/passkey-processing')({
  validateSearch: processingSearchSchema,
  component: PasskeyProcessingRoute
});
function PasskeyProcessingRoute() {
  const $ = _c(35);
  const {
    authToken,
    validationToken,
    scopedAuthToken
  } = Route.useSearch();
  const footprint = useFootprint();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "identify.passkeyProcessing"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation("idv2", t0);
  const hasProceedRef = useRef(false);
  const [outcome, setOutcome] = useState();
  let t1;
  if ($[1] !== authToken || $[2] !== footprint || $[3] !== validationToken) {
    t1 = () => {
      if (hasProceedRef.current) {
        return;
      }
      hasProceedRef.current = true;
      logInfo("Passkey processing complete, proceeding");
      footprint.navigation.identifyComplete({
        authToken,
        validationToken
      });
    };
    $[1] = authToken;
    $[2] = footprint;
    $[3] = validationToken;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const proceedToDone = t1;
  let t2;
  if ($[5] !== proceedToDone) {
    t2 = status => {
      if (status === "completed") {
        logInfo("Passkey registration completed via D2P");
        proceedToDone();
      } else {
        if (status === "canceled") {
          logInfo("Passkey registration cancelled via D2P");
          setOutcome("canceled");
        } else {
          if (status === "failed") {
            logWarn("Passkey D2P session failed");
            setOutcome("failed");
          }
        }
      }
    };
    $[5] = proceedToDone;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== scopedAuthToken || $[8] !== t2) {
    t3 = {
      authToken: scopedAuthToken,
      onStatusChange: t2
    };
    $[7] = scopedAuthToken;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const d2pPoll = useD2pStatusPoll(t3);
  let t4;
  if ($[10] !== d2pPoll) {
    t4 = async () => {
      logInfo("Cancelling passkey D2P session");
      ;
      try {
        await d2pPoll.cancel();
      } catch (t5) {
        const error = t5;
        logWarn("Failed to cancel D2P session", error);
      }
      setOutcome("canceled");
    };
    $[10] = d2pPoll;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const handleCancel = t4;
  let t5;
  if ($[12] !== d2pPoll || $[13] !== handleCancel || $[14] !== outcome) {
    t5 = () => {
      if (outcome) {
        return;
      }
      const passkeyWindow = (window as Window & {
        __fp_passkey_window?: Window;
      }).__fp_passkey_window;
      if (!passkeyWindow || passkeyWindow.closed) {
        return;
      }
      const checkInterval = window.setInterval(() => {
        if (passkeyWindow.closed) {
          window.clearInterval(checkInterval);
          if (isTerminalStatus(d2pPoll.status)) {
            return;
          }
          handleCancel();
        }
      }, 1000);
      return () => window.clearInterval(checkInterval);
    };
    $[12] = d2pPoll;
    $[13] = handleCancel;
    $[14] = outcome;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== outcome) {
    t6 = [outcome];
    $[16] = outcome;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  useEffect(t5, t6);
  if (outcome) {
    let t7;
    if ($[18] !== outcome || $[19] !== proceedToDone) {
      t7 = <PasskeyProcessingResult outcome={outcome} onContinue={proceedToDone} />;
      $[18] = outcome;
      $[19] = proceedToDone;
      $[20] = t7;
    } else {
      t7 = $[20];
    }
    return t7;
  }
  let t7;
  if ($[21] !== t) {
    t7 = t(_temp);
    $[21] = t;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== t) {
    t8 = t(_temp2);
    $[23] = t;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <LoadingSpinner size="xlarge" />;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== t) {
    t10 = t(_temp3);
    $[26] = t;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== handleCancel || $[29] !== t10) {
    t11 = <div className="flex flex-col items-center pt-6 gap-6">{t9}<LinkButton onClick={handleCancel}>{t10}</LinkButton></div>;
    $[28] = handleCancel;
    $[29] = t10;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== t11 || $[32] !== t7 || $[33] !== t8) {
    t12 = <PageShell title={t7} subtitle={t8}>{t11}</PageShell>;
    $[31] = t11;
    $[32] = t7;
    $[33] = t8;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  return t12;
}
function _temp3($_1) {
  return $_1.cancel;
}
function _temp2($_0) {
  return $_0.subtitle;
}
function _temp($) {
  return $.title;
}